// @ts-nocheck
"use client";

import { usePathname } from "next/navigation";
import { useEffect } from "react";

export default function CinematicLayer() {
  const pathname = usePathname();

  // Replay the cinematic intro every time the user lands on "/" — including
  // back-nav from another route. usePathname re-runs this effect on every
  // path change, by which time Next.js has already rendered the new tree
  // (so the pre-rendered #cin-intro from app/page.tsx is in the DOM).
  useEffect(() => {
    if (pathname !== "/") return;
    // Respect session gate — don't replay if user has seen the intro this session,
    // unless ?intro=1 forces it (QA convenience).
    let seen = false;
    try { seen = sessionStorage.getItem("lexvio:intro-seen") === "1"; } catch {}
    const forced = new URLSearchParams(window.location.search).get("intro") === "1";
    if (seen && !forced) return;
    const fn = (window as any).__lexvioReplayIntro;
    if (typeof fn === "function") fn();
  }, [pathname]);

  // Force a hard reload when user comes back to / via browser nav. We flag
  // the session on unmount (when leaving /) and check the flag on mount.
  useEffect(() => {
    const RELOAD_FLAG = "lexvio:reload-on-return";
    try {
      if (sessionStorage.getItem(RELOAD_FLAG) === "1") {
        sessionStorage.removeItem(RELOAD_FLAG);
        window.location.reload();
      }
    } catch {}
    return () => {
      // Only flag if user truly navigated away from / (not a strict-mode
      // pretend-unmount where the URL is still /).
      try {
        if (window.location.pathname !== "/") {
          sessionStorage.setItem(RELOAD_FLAG, "1");
        }
      } catch {}
    };
  }, []);

  useEffect(() => {
    const body = document.body;

    // ── Teardown trackers ──
    const timeouts: number[] = [];
    const intervals: number[] = [];
    const rafs: number[] = [];
    const appended: Element[] = [];
    const observers: IntersectionObserver[] = [];
    const listeners: Array<{ target: EventTarget; type: string; fn: EventListener }> = [];
    let cancelled = false;

    const setT = (fn: () => void, ms: number) => {
      const id = window.setTimeout(fn, ms);
      timeouts.push(id);
      return id;
    };
    const setI = (fn: () => void, ms: number) => {
      const id = window.setInterval(fn, ms);
      intervals.push(id);
      return id;
    };
    const raf = (fn: FrameRequestCallback) => {
      const id = requestAnimationFrame(fn);
      rafs.push(id);
      return id;
    };
    const addEl = (el: Element) => {
      appended.push(el);
      return el;
    };
    const addObs = (o: IntersectionObserver) => {
      observers.push(o);
      return o;
    };
    const addListen = (target: EventTarget, type: string, fn: EventListener, opts?: AddEventListenerOptions) => {
      target.addEventListener(type, fn, opts);
      listeners.push({ target, type, fn });
    };

    // ══ CIN-INTRO: Film slate sequence ══
    function playCinematicIntro() {
      // Countdown timing is now CSS-driven (see globals.css cdStep / cdAction
      // / cinIntroOut). JS only listens for the final cinIntroOut animation
      // to mark body.cin-ready and clean up.
      body.classList.remove("cin-ready", "cin-lb-thin", "cin-lb-full");
      const cinIntro = document.getElementById("cin-intro") as HTMLElement | null;
      if (!cinIntro) return;
      cinIntro.classList.remove("out", "done");
      cinIntro.style.display = "";

      const onIntroEnd = (e: AnimationEvent) => {
        if (e.animationName !== "cinIntroOut") return;
        cinIntro.classList.add("done");
        body.classList.add("cin-ready");
        body.classList.add("cin-lb-thin");
        setT(() => body.classList.remove("cin-lb-thin"), 4000);
        cinIntro.removeEventListener("animationend", onIntroEnd as EventListener);
      };
      cinIntro.addEventListener("animationend", onIntroEnd as EventListener);

      // Skip handlers are attached ONCE at the document level (below, outside
      // playCinematicIntro) via event delegation — so they survive every
      // replay, browser-back, and DOM re-render. No accumulating closures.
    }

    // Expose playCinematicIntro on window so the path-change effect (and
    // anything else that needs to replay the intro) can call it directly.
    (window as any).__lexvioReplayIntro = playCinematicIntro;

    // Skip handler — works in EVERY scenario:
    //   1. CinSkipButton calls window.__lexvioSkipIntro directly (React onClick)
    //   2. Click anywhere on the intro overlay (delegated below)
    //   3. Esc / Enter keypress (handler further down)
    // Looks up #cin-intro at call time so it stays correct across replays.
    const skipIntroNow = () => {
      const live = document.getElementById("cin-intro");
      if (!live || live.classList.contains("done") || live.classList.contains("out")) return;
      live.style.animation = "none";
      live.classList.add("out");
      setT(() => {
        live.classList.add("done");
        body.classList.add("cin-ready");
        body.classList.add("cin-lb-thin");
        setT(() => body.classList.remove("cin-lb-thin"), 4000);
      }, 600);
    };
    (window as any).__lexvioSkipIntro = skipIntroNow;

    const onIntroClick = (ev: Event) => {
      const t = ev.target as Element | null;
      if (!t) return;
      // Skip when clicking anywhere on the intro overlay (the button itself
      // is handled by React's own onClick, but we still catch overlay clicks).
      if (t.closest("#cin-intro") && !t.closest("#cinSkipBtn")) {
        ev.stopPropagation();
        skipIntroNow();
      }
    };
    addListen(document, "click", onIntroClick);

    // Cinematic intro disabled site-wide — cin-ready fires immediately so
    // first paint is the hero, not a 4-second countdown. ?intro=1 query
    // param still forces it, kept as a manual opt-in for a demo/preview
    // without touching code.
    const INTRO_SEEN = "lexvio:intro-seen";
    let seenThisSession = false;
    try { seenThisSession = sessionStorage.getItem(INTRO_SEEN) === "1"; } catch {}
    const forceIntro = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("intro") === "1";

    if (!forceIntro) {
      const live = document.getElementById("cin-intro");
      if (live) { live.classList.add("done"); live.style.display = "none"; }
      body.classList.add("cin-ready");
    } else {
      playCinematicIntro();
      try { sessionStorage.setItem(INTRO_SEEN, "1"); } catch {}
    }

    // Replay on browser back/forward (bfcache restore). pageshow fires with
    // persisted=true; only replay if the user hasn't seen it this session.
    const onPageShow = (e: PageTransitionEvent) => {
      if (!e.persisted) return;
      let seen = false;
      try { seen = sessionStorage.getItem(INTRO_SEEN) === "1"; } catch {}
      if (!seen) playCinematicIntro();
    };
    addListen(window, "pageshow", onPageShow as EventListener);

    // Reload-on-return is handled by the dedicated useEffect above. The
    // popstate listener was unreliable here because CinematicLayer unmounts
    // when navigating off / and the listener went with it.

    // Global Esc/Enter to skip (works across replays since it inspects the
    // current #cin-intro at click time, not a stale reference)
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape" && e.key !== "Enter") return;
      const live = document.getElementById("cin-intro");
      if (!live || live.classList.contains("done")) return;
      live.classList.add("out");
      setT(() => {
        live.classList.add("done");
        body.classList.remove("cin-lb-full");
        body.classList.add("cin-lb-thin");
        body.classList.add("cin-ready");
        setT(() => body.classList.remove("cin-lb-thin"), 4000);
      }, 600);
    };
    addListen(window, "keydown", onKey);

    // (Title card is now created inside playCinematicIntro and replays with it.)

    // Wait for intro to finish, then init everything
    setT(() => {
      if (cancelled) return;
      body.classList.add("ready");

      // ══ SPOTLIGHT ══
      const spotlight = document.createElement("div");
      spotlight.id = "cin-spotlight";
      spotlight.setAttribute("aria-hidden", "true");
      body.appendChild(spotlight); addEl(spotlight);
      let mx = innerWidth / 2, my = innerHeight / 2, sx = mx, sy = my;
      const onMove = (e: MouseEvent) => { mx = e.clientX; my = e.clientY; };
      addListen(document, "mousemove", onMove);
      const loop = () => {
        if (cancelled) return;
        sx += (mx - sx) * 0.08; sy += (my - sy) * 0.08;
        spotlight.style.left = sx + "px"; spotlight.style.top = sy + "px";
        raf(loop);
      };
      loop();

      // ══ VIGNETTE ══
      const vignette = document.createElement("div");
      vignette.id = "cin-vignette";
      vignette.setAttribute("aria-hidden", "true");
      body.appendChild(vignette); addEl(vignette);

      // ══ LETTERBOX ══
      const lbTop = document.createElement("div"); lbTop.className = "cin-lb"; lbTop.id = "cin-lb-top"; lbTop.setAttribute("aria-hidden", "true");
      const lbBot = document.createElement("div"); lbBot.className = "cin-lb"; lbBot.id = "cin-lb-bot"; lbBot.setAttribute("aria-hidden", "true");
      body.appendChild(lbTop); body.appendChild(lbBot);
      addEl(lbTop); addEl(lbBot);

      // ══ ANAMORPHIC FLARE — inject into hero ══
      const hero = document.querySelector(".hero");
      if (hero && !hero.querySelector(".cin-flare")) {
        const flare = document.createElement("div");
        flare.className = "cin-flare";
        flare.setAttribute("aria-hidden", "true");
        hero.appendChild(flare); addEl(flare);
      }

      // ══ DARK-BG SECTION DETECTION (for HUD contrast) ══
      const darkIO = new IntersectionObserver((entries) => {
        entries.forEach(en => {
          if (en.isIntersecting && en.intersectionRatio > 0.5) body.classList.add("cin-on-dark");
        });
      }, { threshold: [0.5] });
      const lightIO = new IntersectionObserver((entries) => {
        entries.forEach(en => {
          if (en.isIntersecting && en.intersectionRatio > 0.5) body.classList.remove("cin-on-dark");
        });
      }, { threshold: [0.5] });
      addObs(darkIO); addObs(lightIO);
      document.querySelectorAll("section, .motion-section, .stats-sec, .cta-sec, .hero, .diff-row").forEach(el => {
        const bg = getComputedStyle(el).backgroundColor;
        const m = bg.match(/\d+/g);
        if (m && m.length >= 3) {
          const lum = +m[0] * 0.299 + +m[1] * 0.587 + +m[2] * 0.114;
          if (lum < 60) darkIO.observe(el);
          else lightIO.observe(el);
        }
      });

      // ══ EDGE MARKS ══
      const edgeMarks = document.createElement("div");
      edgeMarks.className = "edge-marks";
      edgeMarks.innerHTML = '<div class="em em-tl">A01</div><div class="em em-tr">LEXVIO.AI</div><div class="em em-bl">2026</div><div class="em em-br">v5.0</div>';
      body.appendChild(edgeMarks); addEl(edgeMarks);

      // ══ CORNER HUDs ══
      const huds = [
        { cls: "cin-hud cin-hud-tl", id: "cinFrame", text: "00000" },
        { cls: "cin-hud cin-hud-tr", id: "cinTC", text: "00:00:00:00" },
        { cls: "cin-hud cin-hud-bl", text: "LEXVIO · 2026" },
        { cls: "cin-hud cin-hud-br", text: "1.85:1 · 24FPS" },
      ];
      huds.forEach(h => { const el = document.createElement("div"); el.className = h.cls; if (h.id) el.id = h.id; el.textContent = h.text; body.appendChild(el); addEl(el); });

      // Frame counter
      let frame = 0; const startTime = Date.now();
      setI(() => {
        frame++;
        const fEl = document.getElementById("cinFrame");
        const tEl = document.getElementById("cinTC");
        if (fEl) fEl.textContent = String(frame).padStart(5, "0");
        if (tEl) { const e = (Date.now() - startTime) / 1000; tEl.textContent = `${String(Math.floor(e/3600)).padStart(2,"0")}:${String(Math.floor((e%3600)/60)).padStart(2,"0")}:${String(Math.floor(e%60)).padStart(2,"0")}:${String(frame%24).padStart(2,"0")}`; }
      }, 1000 / 24);

      // ══ BURN FLASH ══
      const burn = document.createElement("div"); burn.id = "cin-burn"; body.appendChild(burn); addEl(burn);

      // ══ CHAPTER FLASH CARD ══
      const chapter = document.createElement("div"); chapter.id = "cin-chapter";
      chapter.innerHTML = '<div class="cc-inner"><div class="cc-num"></div><div class="cc-t"></div><div class="cc-s">LEXVIO.AI · 2026</div></div>';
      body.appendChild(chapter); addEl(chapter);

      // ══ CHAPTER SCRUBBER ══
      const scrub = document.createElement("div"); scrub.id = "cin-scrub"; body.appendChild(scrub); addEl(scrub);

      // ══ DIRECTOR'S CUT TOGGLE ══
      const toggle = document.createElement("div"); toggle.id = "cin-toggle";
      toggle.innerHTML = '<button class="on" data-mode="cin">CINEMATIC</button><button data-mode="raw">RAW</button>';
      body.appendChild(toggle); addEl(toggle);
      const onToggleClick = (e: Event) => {
        const b = (e.target as HTMLElement).closest("button"); if (!b) return;
        toggle.querySelectorAll("button").forEach(x => x.classList.remove("on"));
        b.classList.add("on");
        body.classList.toggle("cin-raw", (b as HTMLElement).dataset.mode === "raw");
      };
      addListen(toggle, "click", onToggleClick);

      // ══ BUILD SECTIONS + FEATURES ══
      const sections = document.querySelectorAll("section, .diff-section, .diff-row, .stats-sec, .compete, footer, .story-outer, .hs-outer");
      const chapters: Array<{ el: Element; label: string; num: number }> = [];
      const watermarks = ["Legal", "Compliance", "Tax", "Contracts", "Research", "Redline", "Protect", "Automate"];
      let num = 0;

      sections.forEach((sec) => {
        const titleEl = sec.querySelector("h1, h2, .motion-h, .diff-h, .sh2, .cta-h, .ey, .stats-header-h");
        // Build the chapter label from innerHTML so <br> becomes a space
        // (textContent would concatenate "law." + "Not" with no separator),
        // strip tags + decode entities, normalize whitespace, then cut at
        // the last word boundary at or before 32 chars so we never end
        // with a half-word like "tra".
        const rawHtml = titleEl?.innerHTML || "";
        const cleaned = rawHtml
          .replace(/<br\s*\/?>/gi, " ")
          .replace(/<[^>]+>/g, "")
          .replace(/&amp;/g, "&").replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&nbsp;/g, " ")
          .replace(/&#39;|&apos;/g, "'").replace(/&quot;/g, '"')
          .replace(/\s+/g, " ").trim();
        let label = cleaned;
        if (label.length > 32) {
          const cut = label.lastIndexOf(" ", 32);
          label = (cut > 12 ? label.slice(0, cut) : label.slice(0, 32)).replace(/[.,;:!?-]+$/, "") + "…";
        }
        if (label.length < 3) return;
        num++;
        chapters.push({ el: sec, label, num });

        sec.setAttribute("data-cin-push", "");

        if (!sec.querySelector(".cin-watermark")) {
          const cs = getComputedStyle(sec);
          if (cs.position === "static") (sec as HTMLElement).style.position = "relative";
          if (cs.overflow !== "hidden") (sec as HTMLElement).style.overflow = "hidden";
          const wm = document.createElement("div");
          wm.className = "cin-watermark";
          wm.textContent = watermarks[(num - 1) % watermarks.length];
          sec.appendChild(wm); addEl(wm);
        }

        const isDark = sec.classList.contains("motion-section") || sec.classList.contains("stats-sec") || sec.classList.contains("cta-sec") || sec.matches(".diff-row.dark");
        if (isDark && !sec.querySelector(".cin-sweep")) {
          const sw = document.createElement("div"); sw.className = "cin-sweep"; sec.appendChild(sw); addEl(sw);
        }
      });

      // Scrubber dots
      chapters.forEach((c, i) => {
        const btn = document.createElement("button"); btn.className = "cs"; btn.dataset.idx = String(i);
        btn.innerHTML = `<span class="cs-lbl">${String(c.num).padStart(2, "0")} · ${c.label}</span>`;
        const onScrubClick = () => c.el.scrollIntoView({ behavior: "smooth", block: "start" });
        addListen(btn, "click", onScrubClick);
        scrub.appendChild(btn);
      });

      // ══ CIN-PUSH ══
      const pushIO = new IntersectionObserver((entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting) {
            en.target.classList.remove("cin-near");
            en.target.classList.add("cin-in");
            const sw = en.target.querySelector(".cin-sweep");
            if (sw) sw.classList.add("go");
          } else {
            en.target.classList.remove("cin-in");
            if (en.boundingClientRect.top > 0) en.target.classList.add("cin-near");
          }
        });
      }, { threshold: [0.15, 0.5] });
      addObs(pushIO);
      document.querySelectorAll("[data-cin-push]").forEach(el => pushIO.observe(el));

      // ══ CHAPTER TRACKING ══
      let lastIdx = -1;
      const chObs = new IntersectionObserver((entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting && en.intersectionRatio > 0.35) {
            const idx = chapters.findIndex(c => c.el === en.target);
            if (idx < 0) return;
            scrub.querySelectorAll(".cs").forEach(b => b.classList.remove("active"));
            scrub.querySelector(`[data-idx="${idx}"]`)?.classList.add("active");
            if (idx !== lastIdx && idx > lastIdx && scrollY > 300) {
              lastIdx = idx;
              // Chapter flash card + burn flash disabled — only the scrubber
              // dot (above) still tracks scroll position.
            } else { lastIdx = idx; }
          }
        });
      }, { threshold: [0.35, 0.6] });
      addObs(chObs);
      chapters.forEach(c => chObs.observe(c.el));

      // ══ CIN-WIPE ══
      const wipeTargets = document.querySelectorAll(".motion-h, .diff-h, .cta-h, .sh2, .stats-header-h");
      wipeTargets.forEach(h => {
        if ((h as HTMLElement).dataset.cinWiped || h.querySelector(".hw") || h.querySelector(".cin-wipe")) return;
        (h as HTMLElement).dataset.cinWiped = "1";
        const text = (h.innerHTML || "")
          .replace(/<br\s*\/?>/gi, " ")
          .replace(/<[^>]+>/g, "")
          .replace(/&amp;/g, "&").replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&nbsp;/g, " ")
          .replace(/\s+/g, " ").trim();
        h.innerHTML = "";
        const wrap = document.createElement("span");
        wrap.className = "cin-wipe";
        // Wrap each word in .cin-word so the browser doesn't break a word
        // across two lines between its inline-block character spans. Spaces
        // between words are text nodes — the only line-break opportunities.
        let charIdx = 0;
        text.split(/(\s+)/).forEach((chunk) => {
          if (!chunk) return;
          if (/^\s+$/.test(chunk)) {
            wrap.appendChild(document.createTextNode(" "));
            return;
          }
          const word = document.createElement("span");
          word.className = "cin-word";
          chunk.split("").forEach((ch) => {
            const sp = document.createElement("span");
            sp.className = "cc";
            sp.style.transitionDelay = (charIdx * 22) + "ms";
            sp.textContent = ch;
            word.appendChild(sp);
            charIdx++;
          });
          wrap.appendChild(word);
        });
        h.appendChild(wrap);
      });
      const wipeIO = new IntersectionObserver((entries) => {
        entries.forEach(en => { if (en.isIntersecting) { en.target.querySelector(".cin-wipe")?.classList.add("go"); wipeIO.unobserve(en.target); } });
      }, { threshold: 0.3 });
      addObs(wipeIO);
      // If we're mounting with scroll already past the top (back nav / refresh
      // mid-page), the IO above won't fire for elements that aren't currently
      // intersecting — force-reveal them immediately so the cinematic text
      // isn't stuck at opacity:0/translate-110%.
      if (window.scrollY > 50) {
        wipeTargets.forEach(t => t.querySelector(".cin-wipe")?.classList.add("go"));
      } else {
        wipeTargets.forEach(t => wipeIO.observe(t));
      }

      // ══ PARALLAX on watermarks ══
      const parEls = document.querySelectorAll(".cin-watermark");
      const parUpdate = () => {
        if (cancelled) return;
        const vh = innerHeight;
        parEls.forEach(el => {
          const r = el.getBoundingClientRect();
          const center = r.top + r.height / 2 - vh / 2;
          (el as HTMLElement).style.transform = `translate3d(0,${center * -0.05}px,0)`;
        });
        raf(parUpdate);
      };
      parUpdate();

    }, 4200);

    return () => {
      cancelled = true;
      timeouts.forEach(id => clearTimeout(id));
      intervals.forEach(id => clearInterval(id));
      rafs.forEach(id => cancelAnimationFrame(id));
      observers.forEach(o => o.disconnect());
      listeners.forEach(({ target, type, fn }) => target.removeEventListener(type, fn));
      appended.forEach(el => el.remove());
      body.classList.remove("ready", "cin-lb-full", "cin-lb-thin", "cin-raw", "cin-on-dark");
      try { delete (window as any).__lexvioReplayIntro; } catch {}
      try { delete (window as any).__lexvioSkipIntro; } catch {}
    };
  }, []);

  return null;
}
